/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const DEACTIVATE_CREDENTIAL_TYPE_DISCRIMINATOR = new Uint8Array([
  110, 174, 221, 244, 36, 206, 60, 237,
]);

export function getDeactivateCredentialTypeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    DEACTIVATE_CREDENTIAL_TYPE_DISCRIMINATOR,
  );
}

export type DeactivateCredentialTypeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountCredentialType extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCredentialType extends string
        ? WritableAccount<TAccountCredentialType>
        : TAccountCredentialType,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type DeactivateCredentialTypeInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type DeactivateCredentialTypeInstructionDataArgs = {};

export function getDeactivateCredentialTypeInstructionDataEncoder(): FixedSizeEncoder<DeactivateCredentialTypeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: DEACTIVATE_CREDENTIAL_TYPE_DISCRIMINATOR,
    }),
  );
}

export function getDeactivateCredentialTypeInstructionDataDecoder(): FixedSizeDecoder<DeactivateCredentialTypeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getDeactivateCredentialTypeInstructionDataCodec(): FixedSizeCodec<
  DeactivateCredentialTypeInstructionDataArgs,
  DeactivateCredentialTypeInstructionData
> {
  return combineCodec(
    getDeactivateCredentialTypeInstructionDataEncoder(),
    getDeactivateCredentialTypeInstructionDataDecoder(),
  );
}

export type DeactivateCredentialTypeInput<
  TAccountCredentialType extends string = string,
  TAccountAuthority extends string = string,
> = {
  credentialType: Address<TAccountCredentialType>;
  authority: TransactionSigner<TAccountAuthority>;
};

export function getDeactivateCredentialTypeInstruction<
  TAccountCredentialType extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: DeactivateCredentialTypeInput<
    TAccountCredentialType,
    TAccountAuthority
  >,
  config?: { programAddress?: TProgramAddress },
): DeactivateCredentialTypeInstruction<
  TProgramAddress,
  TAccountCredentialType,
  TAccountAuthority
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credentialType: { value: input.credentialType ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credentialType),
      getAccountMeta(accounts.authority),
    ],
    data: getDeactivateCredentialTypeInstructionDataEncoder().encode({}),
    programAddress,
  } as DeactivateCredentialTypeInstruction<
    TProgramAddress,
    TAccountCredentialType,
    TAccountAuthority
  >);
}

export type ParsedDeactivateCredentialTypeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    credentialType: TAccountMetas[0];
    authority: TAccountMetas[1];
  };
  data: DeactivateCredentialTypeInstructionData;
};

export function parseDeactivateCredentialTypeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedDeactivateCredentialTypeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { credentialType: getNextAccount(), authority: getNextAccount() },
    data: getDeactivateCredentialTypeInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
